/**
 * File: useNavGroupController.ts
 * Path: apps/admin/src/modules/layout/hooks/useNavGroupController.ts
 * Description: A sidebar heading (CRM, CMS, Tools...) that opens and closes its links. Closed headings are
 * remembered; going to one of a heading's pages opens it. With the sidebar shrunk to icons every icon shows.
 */

import { useEffect, useId, useMemo } from "react";
import type { AdminNavItem } from "@idk/config";
import { useUIStore } from "@/modules/app/hooks/useUiState";
import { useNavActiveState } from "@/modules/layout/hooks/useNavActiveState";

export const useNavGroupController = (group: AdminNavItem, isOpen: boolean) => {
  const { pathname, isActive } = useNavActiveState();
  const { closedNavGroups, toggleNavGroup, openNavGroup } = useUIStore();
  const listId = useId();
  const children = useMemo(() => group.children ?? [], [group.children]);
  const closed = closedNavGroups.includes(group.label);

  // Arriving on one of its pages opens the heading; it can still be closed after.
  useEffect(() => {
    if (children.some((child) => isActive(child.href))) openNavGroup(group.label);
  }, [pathname, isActive, children, openNavGroup, group.label]);

  const expanded = !isOpen || !closed;

  return { children, expanded, listId, toggle: () => toggleNavGroup(group.label) };
};
